import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { standardRequest } from 'shared/utils/request';
import serializeParams from 'shared/utils/paramsSerializer';
import { useCompanyInfoContext } from 'shared/components/company_info/company_info_context';
import {
  type CompanyProfile,
  type CompanyProfilesResponse,
} from 'shared/components/company_info/types';

// One cache entry per employer name, shared across rows. `null` is a cached
// negative match, distinct from "not fetched yet" (undefined).
const profileKey = (name: string) => ['companyProfile', name];

// Mirrors `TalentMatching::Params::CompanyProfilesParams::MAX_NAMES`, which
// truncates rather than rejects. Sending more than this in one request would
// cache the excess as confident misses.
const MAX_NAMES_PER_REQUEST = 25;

const inRequestSizedBatches = (names: string[]) =>
  Array.from(
    { length: Math.ceil(names.length / MAX_NAMES_PER_REQUEST) },
    (_, index) =>
      names.slice(
        index * MAX_NAMES_PER_REQUEST,
        (index + 1) * MAX_NAMES_PER_REQUEST
      )
  );

const requestProfiles = async (path: string, names: string[]) => {
  const { data } = await standardRequest.get(
    `${path}?${serializeParams({ names })}`
  );
  const companies: CompanyProfilesResponse['companies'] = data?.companies ?? {};
  return companies;
};

/**
 * Warms the cache for a whole table row's employers in one request. Called on row
 * mouse-enter so the lookup is already resolved by the time the pointer reaches a
 * company name and the card's open delay elapses.
 */
export const usePrefetchCompanyProfiles = () => {
  const queryClient = useQueryClient();
  const { canViewCompanyInfo, companyProfilesPath } = useCompanyInfoContext();

  return useCallback(
    async (names: string[]) => {
      if (!canViewCompanyInfo) {
        return;
      }

      const missing = [...new Set(names)].filter((name) => {
        const state = queryClient.getQueryState(profileKey(name));
        return (
          !state ||
          (state.data === undefined && state.fetchStatus !== 'fetching')
        );
      });
      if (missing.length === 0) {
        return;
      }

      const batches = inRequestSizedBatches(missing).map((batchNames) => ({
        batchNames,
        profiles: requestProfiles(companyProfilesPath, batchNames),
      }));

      // Per-name keys resolving from one shared request let a card opening
      // mid-flight join the batch instead of issuing its own lookup.
      await Promise.all(
        batches.flatMap(({ batchNames, profiles }) =>
          batchNames.map((name) =>
            queryClient.prefetchQuery({
              queryKey: profileKey(name),
              queryFn: () =>
                profiles.then((companies) => companies[name] ?? null),
              staleTime: Infinity,
            })
          )
        )
      );
    },
    [canViewCompanyInfo, companyProfilesPath, queryClient]
  );
};

/**
 * Reads one employer's profile. Normally resolves straight from the cache the row
 * prefetch seeded; the single-name request is the fallback for reaching a company
 * without hovering its row first, such as keyboard focus.
 */
export const useCompanyProfile = (name: string, enabled: boolean) => {
  const { canViewCompanyInfo, companyProfilesPath } = useCompanyInfoContext();

  const { data, isLoading, isError } = useQuery<CompanyProfile | null>({
    queryKey: profileKey(name),
    queryFn: async () => {
      const companies = await requestProfiles(companyProfilesPath, [name]);
      return companies[name] ?? null;
    },
    enabled: enabled && canViewCompanyInfo && !!name,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // A failed lookup is not a miss: the endpoint answers 503 when the dataset is
  // unreachable, and the card says so rather than reporting the employer absent.
  return { profile: data ?? null, isLoading, isUnavailable: isError };
};
